import React, { useEffect, useState } from 'react';
import api from '../lib/api';

// Venue-facing panel: pending rider swap confirmations (Rule 3 §5.3).
// Consumes GET/POST /api/venues/me/swap-confirmations (commit 27d4f5e).
// Only fields the endpoint actually returns are rendered — per recon the
// swap doc has requested_model, category, artist_profile_id, id, status.
// There is no persisted "proposed equivalent" model and no artist name join,
// so we show the requested model + its class tag, and the raw artist_profile_id.

const panelStyle = {
  background: 'var(--s8-bg-card)',
  border: '1px solid var(--s8-border-card)',
  borderRadius: 'var(--s8-radius-lg)',
  padding: '20px',
  fontFamily: 'var(--s8-font-body)',
  color: 'var(--s8-text-body)',
};

const headerRowStyle = {
  borderBottom: '2px solid var(--s8-amber)',
  paddingBottom: '12px',
};

const headerStyle = {
  fontFamily: 'var(--s8-font-display)',
  fontWeight: 600,
  fontSynthesis: 'none',
  fontSize: '28px',
  letterSpacing: '-0.01em',
  lineHeight: 1.15,
  color: 'var(--s8-text-main)',
  margin: 0,
};

const listStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '16px',
};

const itemStyle = {
  border: '1px solid var(--s8-border-card)',
  borderRadius: 'var(--s8-radius-md)',
  background: 'var(--s8-bg-base)',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const modelLineStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
};

const modelStyle = {
  fontFamily: 'var(--s8-font-data)',
  fontSize: '15px',
  color: 'var(--s8-text-main)',
};

const classTagStyle = {
  fontFamily: 'var(--s8-font-data)',
  fontSize: '11px',
  textTransform: 'lowercase',
  padding: '3px 9px',
  borderRadius: 'var(--s8-radius-pill)',
  background: 'var(--s8-accent-dim)',
  color: 'var(--s8-amber)',
  border: '1px solid var(--s8-border)',
  whiteSpace: 'nowrap',
};

const descStyle = {
  fontFamily: 'var(--s8-font-body)',
  fontSize: 'var(--s8-fs-caption)',
  color: 'var(--s8-text-dim)',
};

const secondLineStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '12px',
};

const requestedByStyle = {
  fontFamily: 'var(--s8-font-body)',
  fontSize: 'var(--s8-fs-caption)',
  color: 'var(--s8-text-dim)',
};

const idStyle = {
  fontFamily: 'var(--s8-font-data)',
  color: 'var(--s8-text-muted)',
};

const actionsStyle = { display: 'flex', gap: '8px' };

const btnBase = {
  fontFamily: 'var(--s8-font-body)',
  fontSize: 'var(--s8-fs-caption)',
  fontWeight: 600,
  padding: '8px 16px',
  borderRadius: 'var(--s8-radius-sm)',
  cursor: 'pointer',
  border: '1px solid transparent',
};

const confirmBtnStyle = {
  ...btnBase,
  background: 'var(--s8-green-ok)',
  color: 'var(--s8-bg-base)',
};

const denyBtnStyle = {
  ...btnBase,
  background: 'transparent',
  color: 'var(--s8-red)',
  border: '1px solid var(--s8-red)',
};

const disabledStyle = { opacity: 0.5, cursor: 'default' };

const mutedTextStyle = {
  marginTop: '16px',
  fontFamily: 'var(--s8-font-body)',
  fontSize: 'var(--s8-fs-body)',
  color: 'var(--s8-text-dim)',
};

const noteStyle = {
  marginTop: '12px',
  fontFamily: 'var(--s8-font-body)',
  fontSize: 'var(--s8-fs-caption)',
  color: 'var(--s8-green-ok)',
};

const errorTextStyle = {
  marginTop: '12px',
  fontFamily: 'var(--s8-font-body)',
  fontSize: 'var(--s8-fs-caption)',
  color: 'var(--s8-red-flag)',
};

export default function VenueSwapConfirmations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [postError, setPostError] = useState(null);
  const [resolving, setResolving] = useState({}); // id -> true while its POST is in flight
  const [resolvedNote, setResolvedNote] = useState(null); // brief inline confirmation

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Defaults to status=pending server-side.
        const res = await api.get('/venues/me/swap-confirmations');
        if (active) setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (active) setLoadError('Could not load pending swap requests.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const respond = async (item, status) => {
    setPostError(null);
    setResolving((r) => ({ ...r, [item.id]: true }));
    try {
      await api.post(`/venues/me/swap-confirmations/${item.id}/respond`, { status });
      // Optimistic: drop the item and surface a brief inline confirmation.
      setItems((list) => list.filter((it) => it.id !== item.id));
      setResolvedNote(
        status === 'confirmed'
          ? `Swap confirmed — ${item.requested_model}.`
          : `Swap declined — ${item.requested_model}.`
      );
    } catch (e) {
      setPostError('Could not submit your response. Please try again.');
      setResolving((r) => {
        const next = { ...r };
        delete next[item.id];
        return next;
      });
    }
  };

  return (
    <section style={panelStyle} data-testid="venue-swap-confirmations">
      <div style={headerRowStyle}>
        <h2 style={headerStyle}>Pending Swap Requests</h2>
      </div>

      {resolvedNote && (
        <p style={noteStyle} data-testid="swap-resolved-note">{resolvedNote}</p>
      )}

      {loading ? (
        <p style={mutedTextStyle}>Loading pending swap requests…</p>
      ) : loadError ? (
        <p style={errorTextStyle} data-testid="swap-load-error">{loadError}</p>
      ) : items.length === 0 ? (
        <p style={mutedTextStyle} data-testid="swap-empty">No pending swap requests.</p>
      ) : (
        <div style={listStyle}>
          {items.map((item) => {
            const busy = !!resolving[item.id];
            return (
              <div key={item.id} style={itemStyle} data-testid="swap-item">
                <div style={modelLineStyle}>
                  <span style={modelStyle}>{item.requested_model}</span>
                  {item.category && <span style={classTagStyle}>{item.category}</span>}
                  <span style={descStyle}>same-class swap</span>
                </div>
                <div style={secondLineStyle}>
                  <span style={requestedByStyle}>
                    requested by <span style={idStyle}>{item.artist_profile_id}</span>
                  </span>
                  <span style={actionsStyle}>
                    <button
                      type="button"
                      style={busy ? { ...confirmBtnStyle, ...disabledStyle } : confirmBtnStyle}
                      disabled={busy}
                      onClick={() => respond(item, 'confirmed')}
                      data-testid="swap-confirm-btn"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      style={busy ? { ...denyBtnStyle, ...disabledStyle } : denyBtnStyle}
                      disabled={busy}
                      onClick={() => respond(item, 'declined')}
                      data-testid="swap-deny-btn"
                    >
                      Deny
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {postError && (
        <p style={errorTextStyle} data-testid="swap-post-error">{postError}</p>
      )}
    </section>
  );
}
